import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import stateSelector from 'reduxModules/school/selectors'
import { schoolDispatchers as dispatchers } from 'reduxModules/school/dispatchers'

import TestimonialVideo from '../generic/TestimonialVideos'

import SubscribeDialogCTA from '../App/SubscribeDialogCTA'

import './styles.css'
import HeroBanner from './HeroBanner'
import WhatWeTeach from './WhatWeTeach'

import Artists from './Artists'

import SchoolOurStoryDesktop from './assets/1-story-desktop.jpg'
import SchoolOurStoryMobile from './assets/1-story-mobile.jpg'
import SchoolOurHistoryDesktop from './assets/2-history-desktop.jpg'
import SchoolOurHistoryMobile from './assets/2-history-mobile.jpg'
import AboutUsDesktop from './assets/3-about-us-desktop.jpg'
import AboutUsMobile from './assets/3-about-us-mobile.jpg'

import Credits from './Credits'

const videos = {
  school: {
    video:
      'https://d35pwikzwzmbep.cloudfront.net/videos/about/2021-BRAND-VIDEO-2min-a-2021-BRAND-VIDEO-2min-a-1280x720-1200kbps-music.mp4',
    subtitles: [
      {
        file: 'https://d35pwikzwzmbep.cloudfront.net/videos/about/2021-BRAND-VIDEO-2min-a-2021-BRAND-VIDEO-2min-a-1280x720-1200kbps-music-en.vtt',
        label: 'EN'
      },
      {
        file: 'https://d35pwikzwzmbep.cloudfront.net/videos/about/2021-BRAND-VIDEO-2min-a-2021-BRAND-VIDEO-2min-a-1280x720-1200kbps-music-es.vtt',
        label: 'ES'
      }
    ]
  },
  history: {
    video:
      'https://d35pwikzwzmbep.cloudfront.net/videos/about/Stan_Winston_Studio_Promo_Video.mp4',
    subtitles: [
      {
        file: 'https://d35pwikzwzmbep.cloudfront.net/videos/about/Stan_Winston_Studio_Promo_Video-en.vtt',
        label: 'EN'
      },
      {
        file: 'https://d35pwikzwzmbep.cloudfront.net/videos/about/Stan_Winston_Studio_Promo_Video-es.vtt',
        label: 'ES'
      }
    ]
  }
}

export function AboutUs({ isMobile }) {
  const navigate = useNavigate()
  return (
    <div id="AboutUs">
      <Helmet>
        <title>
          Imagine. Learn. Create. | Stan Winston School of Character Arts
        </title>
        <meta
          name="description"
          content="Stan Winston School is the world's premiere online destination for learning the art & technology of character creation from Award-winning industry professionals."
        />
        <meta property="og:title" content="Imagine. Learn. Create." />
        <meta
          property="og:description"
          content="Online Instruction with Award-winning Industry Professionals. Wherever you are. On your own time. At your own pace."
        />
      </Helmet>
      <HeroBanner
        title="Stan Winston School Of Character Arts"
        bannerDesktop={SchoolOurStoryDesktop}
        bannerMobile={SchoolOurStoryMobile}
        videoUrl={videos.school.video}
        subtitleUrl={videos.school.subtitles}
        hasOverlay
      />
      <section className="about-us-main-tagline">
        <p>
          Stan Winston School is the world&apos;s premier online destination for
          learning the art and technology of character creation from
          Hollywood&apos;s leading artists and technical wizards. Creating
          compelling characters requires a vast range of skills and techniques,
          from big-budget cinematic approaches to DIY methods you can practice
          in your home workshop. With 1000+ hours of on-demand video instruction
          to choose from, students can subscribe to gain instant, anytime access
          to our entire course library.
        </p>
      </section>
      <h2>What We Teach</h2>
      <section className="about-us-what-we-teach">
        <WhatWeTeach />
      </section>
      <section className="about-us-cta">
        <button
          className="subscribe-cta-banner__cta btn -wide -orange"
          type="button"
          onClick={() => {
            navigate('/tutorials/')
          }}
          aria-label="Explore Our Courses"
          tabIndex="0"
        >
          Explore Our Courses
        </button>
      </section>
      <h2>What Students Say About Us</h2>
      <section className="about-us-testimonials">
        <TestimonialVideo />
      </section>
      <section className="about-us-cta">
        <SubscribeDialogCTA>
          <div className="subscribe-cta-banner__cta btn -wide -orange">
            Start Your Journey
          </div>
        </SubscribeDialogCTA>
      </section>
      <h2>Some of Our Instructor&apos;s Credits</h2>
      <Credits />
      <h2>Featured Instructors</h2>
      <Artists />
      <h2>Our History</h2>
      <HeroBanner
        bannerDesktop={SchoolOurHistoryDesktop}
        bannerMobile={SchoolOurHistoryMobile}
        videoUrl={videos.history.video}
        subtitleUrl={videos.history.subtitles}
        hasOverlay
      />
      <section className="about-us-our-history-tagline">
        <p>
          For over 50 years, the name Stan Winston has been synonymous with
          iconic fantasy characters, including the killer cyborgs of The
          TERMINATOR series, the extraterrestrial monstrosities of ALIENS, the
          alien hunter from the PREDATOR series, the prehistoric giants from the
          JURASSIC PARK films, and the iconic suits from IRON MAN. By
          relentlessly pushing the limits of art, technology & imagination, Stan
          Winston Studio continually set new standards of excellence and
          innovation for character creators worldwide. After Stan&apos;s
          untimely passing in 2008, the Winston family founded Stan Winston
          School of Character Arts to preserve Stan&apos;s legacy by inspiring
          and fostering creativity in a new generation of character creators.
          Through connecting the world&apos;s finest artists and technicians
          with students worldwide, our mission is to empower aspiring artists
          and technical wizards to push the boundaries of character creation for
          years to come.
        </p>
      </section>
      <section className="final-cta">
        <div
          className="final-cta-background"
          style={{
            backgroundImage: `url(${isMobile ? AboutUsMobile : AboutUsDesktop}`
          }}
        >
          <div className="overlay" />
          <div className="final-cta-content">
            <div className="final-cta-content-tagline">Join Now!</div>
            <SubscribeDialogCTA>
              <div className="subscribe-cta-banner__cta btn -wide -orange">
                Start My Free Trial
              </div>
            </SubscribeDialogCTA>
          </div>
        </div>
      </section>
    </div>
  )
}

AboutUs.propTypes = {
  isMobile: PropTypes.bool
}

export default connect(stateSelector, dispatchers)(AboutUs)
